export const YOUTUBE_CHANNEL_ID_PATTERN = /^UC[A-Za-z0-9_-]{22}$/;

export type StableVideoChannelId = `youtube:${string}`;

/** Names and slugs can change; this key follows the official provider identity. */
export function stableVideoChannelId(youtubeChannelId: string): StableVideoChannelId | null {
  return YOUTUBE_CHANNEL_ID_PATTERN.test(youtubeChannelId)
    ? `youtube:${youtubeChannelId}`
    : null;
}

export const VIDEO_LANES = [
  "Markets",
  "Breaking",
  "Global",
  "Weather",
  "Policy",
  "Science",
] as const;

export type VideoLane = (typeof VIDEO_LANES)[number];

export interface PermanentVideoChannel {
  slug: string;
  name: string;
  shortName: string;
  description: string;
  region: string;
  categories: readonly VideoLane[];
  youtubeChannelId: string;
  officialChannelUrl: string;
  provider: "youtube";
  sourceUrl: string;
  /** Legacy identity/player-render audit date, not a current playback check. */
  lastVerifiedOn: string;
  resolutionMethod: "official-channel-live";
  indexable: true;
}

const channelUrl = (youtubeChannelId: string) =>
  `https://www.youtube.com/channel/${youtubeChannelId}`;

/**
 * The public roster is the approved identity source. The original HTTP-origin
 * canary rendered these players; current playback requires separate dated evidence.
 */
const VIDEO_CHANNEL_DEFINITIONS = [
  {
    slug: "bloomberg",
    name: "Bloomberg Television",
    shortName: "Bloomberg",
    description: "Live business, markets, economics, and policy coverage from Bloomberg Television.",
    region: "United States / Global",
    categories: ["Markets", "Policy", "Global"],
    youtubeChannelId: "UCIALMKvObZNtJ6AmdCLP7Lg",
  },
  {
    slug: "abc-news",
    name: "ABC News",
    shortName: "ABC",
    description: "United States breaking news, public affairs, and major-event coverage from ABC News.",
    region: "United States",
    categories: ["Breaking", "Policy", "Weather"],
    youtubeChannelId: "UCBi2mrWuNuyYy4gbM6fU18Q",
  },
  {
    slug: "nbc-news",
    name: "NBC News",
    shortName: "NBC",
    description: "United States breaking news, politics, and weather coverage from NBC News.",
    region: "United States",
    categories: ["Breaking", "Policy", "Weather"],
    youtubeChannelId: "UCeY0bbntWzzVIaj2z3QigXg",
  },
  {
    slug: "dw-news",
    name: "DW News",
    shortName: "DW",
    description: "International news and analysis from Deutsche Welle's English-language service.",
    region: "Germany / Global",
    categories: ["Global", "Policy", "Science"],
    youtubeChannelId: "UCknLrEdhRCp1aegoMqRaCZg",
  },
  {
    slug: "france-24-english",
    name: "France 24 English",
    shortName: "France 24",
    description: "English-language international news from France 24.",
    region: "France / Global",
    categories: ["Global", "Breaking", "Policy"],
    youtubeChannelId: "UCQfwfsi5VrQ8yKZ-UWmAEFg",
  },
  {
    slug: "al-jazeera-english",
    name: "Al Jazeera English",
    shortName: "Al Jazeera",
    description: "English-language global news and field reporting from Al Jazeera.",
    region: "Qatar / Global",
    categories: ["Global", "Breaking", "Policy"],
    youtubeChannelId: "UCNye-wNBqNL5ZzHSJj3l8Bg",
  },
  {
    slug: "euronews-english",
    name: "Euronews English",
    shortName: "Euronews",
    description: "English-language European and international news from Euronews.",
    region: "Europe / Global",
    categories: ["Global", "Policy", "Breaking"],
    youtubeChannelId: "UCSrZ3UV4jOidv8ppoVuvW9Q",
  },
  {
    slug: "cna",
    name: "CNA",
    shortName: "CNA",
    description: "Asia-focused breaking and business news from Channel NewsAsia.",
    region: "Singapore / Asia",
    categories: ["Global", "Breaking", "Markets"],
    youtubeChannelId: "UC83jt4dlz1Gjl58fzQrrKZg",
  },
] as const satisfies readonly Omit<
  PermanentVideoChannel,
  "officialChannelUrl" | "provider" | "sourceUrl" | "lastVerifiedOn" | "resolutionMethod" | "indexable"
>[];

export const VIDEO_CHANNELS: readonly PermanentVideoChannel[] = VIDEO_CHANNEL_DEFINITIONS.map((channel) => ({
  ...channel,
  officialChannelUrl: channelUrl(channel.youtubeChannelId),
  provider: "youtube" as const,
  sourceUrl: channelUrl(channel.youtubeChannelId),
  lastVerifiedOn: "2026-08-16",
  resolutionMethod: "official-channel-live" as const,
  indexable: true as const,
}));

export const VIDEO_CHANNEL_IDENTITIES: readonly PermanentVideoChannel[] = VIDEO_CHANNELS;

export function getVideoChannel(slug: string): PermanentVideoChannel | undefined {
  return VIDEO_CHANNELS.find((channel) => channel.slug === slug);
}

export function buildYouTubeLiveEmbedUrl(youtubeChannelId: string): string | null {
  if (!YOUTUBE_CHANNEL_ID_PATTERN.test(youtubeChannelId)) return null;

  const url = new URL("https://www.youtube-nocookie.com/embed/live_stream");
  url.searchParams.set("channel", youtubeChannelId);
  url.searchParams.set("autoplay", "1");
  url.searchParams.set("mute", "1");
  url.searchParams.set("playsinline", "1");
  return url.toString();
}

export function isAllowedVideoEmbedUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const allowedParameters = new Set(["channel", "autoplay", "mute", "playsinline"]);
    const parameterKeys = [...url.searchParams.keys()];
    if (
      url.protocol !== "https:" ||
      url.hostname !== "www.youtube-nocookie.com" ||
      url.port !== "" ||
      url.username !== "" ||
      url.password !== "" ||
      url.pathname !== "/embed/live_stream" ||
      url.hash !== "" ||
      parameterKeys.length !== allowedParameters.size ||
      parameterKeys.some((key) => !allowedParameters.has(key))
    ) {
      return false;
    }

    const channelId = url.searchParams.get("channel");
    return (
      channelId !== null &&
      YOUTUBE_CHANNEL_ID_PATTERN.test(channelId) &&
      url.searchParams.get("autoplay") === "1" &&
      url.searchParams.get("mute") === "1" &&
      url.searchParams.get("playsinline") === "1"
    );
  } catch {
    return false;
  }
}

export function getVideoEmbedUrl(channel: PermanentVideoChannel): string | null {
  const url = buildYouTubeLiveEmbedUrl(channel.youtubeChannelId);
  return url && isAllowedVideoEmbedUrl(url) ? url : null;
}

const SIGNAL_CATEGORY_TO_LANE: Readonly<Record<string, VideoLane>> = {
  weather: "Weather",
  earthquake: "Weather",
  health: "Breaking",
  geopolitics: "Global",
  "market-news": "Markets",
  markets: "Markets",
  "fixed-income": "Markets",
  forex: "Markets",
  energy: "Markets",
  crypto: "Markets",
  macro: "Markets",
  "supply-chain": "Markets",
  regulation: "Policy",
  breaking: "Breaking",
  global: "Global",
  policy: "Policy",
  science: "Science",
};

const VIDEO_RECOMMENDATION_ORDER: Readonly<Record<VideoLane, readonly string[]>> = {
  Markets: ["bloomberg", "cna", "dw-news"],
  Breaking: ["abc-news", "nbc-news", "al-jazeera-english", "france-24-english"],
  Global: ["al-jazeera-english", "dw-news", "france-24-english", "euronews-english"],
  Weather: ["abc-news", "nbc-news", "dw-news"],
  Policy: ["abc-news", "nbc-news", "bloomberg", "dw-news"],
  Science: ["dw-news", "abc-news"],
};

export interface VideoRecommendationSignal {
  source?: string;
  category?: string;
  title?: string;
}

/** Pure category-to-channel recommendation. It never performs provider discovery. */
export function recommendVideoChannel(
  signal?: VideoRecommendationSignal,
  channels: readonly PermanentVideoChannel[] = VIDEO_CHANNELS,
): PermanentVideoChannel | undefined {
  const lane = SIGNAL_CATEGORY_TO_LANE[(signal?.category ?? "").trim().toLowerCase()] ?? "Markets";
  const channelsBySlug = new Map(channels.map((channel) => [channel.slug, channel] as const));

  for (const slug of VIDEO_RECOMMENDATION_ORDER[lane]) {
    const channel = channelsBySlug.get(slug);
    if (channel) return channel;
  }

  return channels[0];
}
